
//#region Util Imports

import * as _ from "underscore"

//#endregion

//#region Angular Imports

import { StateService } from '@uirouter/angularjs'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion

import { Api, LocalDictionaryFactory } from './../api/api.module'
import { ColumnModel } from '../api/api.models'

import { LoggerService } from './../common/common.module'

import { CommonHelper, EventsNames, LocalizationHelper, ToastHelper, ConfigHelper, TemplateHelper } from '../../helpers/'

import { Activity, Enums } from '../../models/common/'
import { OpSupplier } from "../../models/core"

import { SupplierService } from "./../../services/supplier"
import { SystemService } from "../../services/system"
import { UserSessionService } from "../../services/user-session"

import { GridButtonsOptions, GridController, GridTypes } from "../common/grid"
import { BaseGridController, ScopeBaseGrid } from '../common/common.base.grid.controller'
import { IIMRConfirmationPopup, IMRConfirmationPopupInitializedArgs, IMRConfirmationPopupOptions } from "../common/confirmation-popup2"

export class SuppliersController extends BaseGridController {

    //#region Costs

    private get SUPPLIERS_GRID_NAME(): string { return 'SUPPLIERS_GRID' }

    //#endregion


    //#region Members    

    public confirmationPopupOptions: IMRConfirmationPopupOptions

    private grid: GridController<OpSupplier>
    private confirmationPopup: IIMRConfirmationPopup

    //#endregion


    //#region Ctor

    constructor(
        protected $scope: ScopeBaseGrid,
        $state: StateService,
        $timeout: ng.ITimeoutService,
        api: Api,
        private logger: LoggerService,
        localDictionary: LocalDictionaryFactory,
        private suppliersService: SupplierService,
        protected systemService: SystemService,
        protected userSessionService: UserSessionService,
        referenceType: Enums.ReferenceType,
        private moduleName: Enums.SimaxModuleName
    ) {
        super($scope, $timeout, $state, userSessionService, systemService, api, localDictionary, null, referenceType)

        this.setConfirmationPopup()
        this.initializeEvents()
        this.initializeMainGrid()
    }

    //#endregion


    //#region Events

    private onAdd(): void {
        this.suppliersService.openCreate()
    }

    private onConfirmDelete(): void {
        if (_.size(this.grid.selectedItems) === 1) {
            this.suppliersService
                .deleteSupplier(this.grid.selectedItem.idSupplier)
                .then(() => {
                    this.$scope.$root.$broadcast(EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName))
                })
                .fail((error: any) => {
                    this.logger.log(error)
                })
        }
    }

    private onDelete(): void {
        this.confirmationPopup.show()
    }

    private onDeleted(): void {
        this.dataGrid.refresh()
    }

    private onEdit(): void {
        if (this.dataGrid.getSelectedRowKeys().length == 1) {
            this.suppliersService.openEdit(this.grid.selectedItemId)
        } else {
            ToastHelper.showInfo(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.BulkEditIsUnsupported))
        }
    }

    private onInitialized(e: any, grid: GridController<OpSupplier>): void {
        this.dataGrid = e.component
        this.grid = grid

        this.setGridId(e.tableId)
    }

    private onShowDetails(): void {
        this.systemService.openDetails({
            moduleName: this.moduleName,
            referenceObjectId: this.grid.selectedItemId,
            pageItemsIds: this.grid.pageItemsIds,
        })
    }

    //#endregion

    //#region IMRConfirmationPopup

    private setConfirmationPopup(): void {
        this.confirmationPopupOptions = {
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETING, this.moduleName),
            content: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CONFIRM_DELETE, this.moduleName),

            onInitialized: (e: IMRConfirmationPopupInitializedArgs) => {
                this.confirmationPopup = e.component
            },

            onConfirmed: () => {
                this.onConfirmDelete()
            }
        }
    }

    //#endregion

    //#region Setup

    private getContextMenuItems(isTouchScreen: boolean = false): any[] {
        let items = []

        if (this.userSessionService.getLoggedOperator().hasPermission(Activity.SUPPLIER_EDIT)) {
            items.push({
                beginGroup: true,
                template: TemplateHelper.getContextMenuItem(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Edit), ConfigHelper.Icon.EDIT, this.userSessionService),

                onItemClick: (e: any) => this.onEdit(),
            })
        }

        return this.mergeWithDefaultItems(isTouchScreen, items)
    }

    private getGridButtonsOptions(): GridButtonsOptions {
        return {
            enabled: true,

            addPermissions: this.userSessionService.getLoggedOperator().hasPermission(Activity.SUPPLIER_ADD) ? "['" + Activity[Activity.SUPPLIER_ADD] + "']" : null,
            deletePermissions: this.userSessionService.getLoggedOperator().hasPermission(Activity.SUPPLIER_DELETE) ? "['" + Activity[Activity.SUPPLIER_DELETE] + "']" : null,
            editPermissions: this.userSessionService.getLoggedOperator().hasPermission(Activity.SUPPLIER_EDIT) ? "['" + Activity[Activity.SUPPLIER_EDIT] + "']" : null,
            showDetailsPermissions: this.userSessionService.getLoggedOperator().hasPermission(Activity.SUPPLIER_LIST) ? "['" + Activity[Activity.SUPPLIER_LIST] + "']" : null,

            onAddClick: () => this.onAdd(),
            onDeleteClick: () => this.onDelete(),
            onEditClick: () => this.onEdit(),
            onShowClick: (e: any) => this.onShowDetails(),
        }
    }    

    private initializeEvents(): void {
        this.$scope.$on(EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CONFIRMED, this.moduleName), (event: ng.IAngularEvent, ...args: any[]) => {
            this.grid.dxGrid.refresh()
        })

        this.$scope.$on(EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName), (event: ng.IAngularEvent, ...args: any[]) => {
            this.onDeleted()
        })

        this.$scope.$on(EventsNames.CLOSE_CREATE_EDIT_POPUP, (event: ng.IAngularEvent, ...args: any[]) => this.dataGrid.refresh())

        this.$scope.showDetails = (e: any) => {
            this.onShowDetails()
        }
    }

    private initializeMainGrid(): void {
        this.suppliersService
            .getMainGridColumns()
            .then((columns: ColumnModel[], defaultColumns: ColumnModel[]) => {
                this.$scope.mainGridOptions = {
                    addCustomFilters: true,
                    buttonsOptions: this.getGridButtonsOptions(),
                    columnsDictionary: columns,
                    defaultColumnsDictionary: defaultColumns,
                    getDataSourceMethod: this.userSessionService.getDataProvider().Supplier.getSupplierFilter,
                    idReferenceType: this.referenceType,
                    localStorageVarForSavedEntity: CommonHelper.localStorageVarForSavedEntity.supplierSaved,
                    name: this.SUPPLIERS_GRID_NAME,
                    preExecuteClearFilter: this.preExecuteGridClearFilter,
                    referenceId: this.referenceType,
                    type: GridTypes.MAIN,

                    allowColumnReordering: !this.userSessionService
                        .getLoggedOperator()
                        .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.SupplierModuleMainGrid ),

                    contextMenuItems: (isTouchScreen: boolean = false) => this.getContextMenuItems(isTouchScreen),
                    onDataReady: (data: any) => this.onDataReady(data),
                    onInitialized: (e: any, grid: GridController<OpSupplier>) => this.onInitialized(e, grid),
                    onRowDoubleClick: (e: any) => this.onShowDetails(),
                }
            })
    }

    // #endregion
}